import { agentRunStatusViewSchema, STAFF_COPILOT_META_PART_TYPE, staffCopilotMetaPartV1Schema, staffCopilotRunBodySchema, staffCopilotSessionSchema, } from "@lafayette/schemas";
import { createSseParser } from "./agent-stream";
import { createIdempotencyKey } from "./idempotency-key";
import { httpErrorOf, readJson } from "./json-transport";
import { createMemoryTransport, MEMORY_ERRORS, MemoryApiError, MemoryApiNetworkError, MemoryApiResponseError, } from "./memory-transport";
const sessionPath = (conversationId) => `/staff/conversations/${encodeURIComponent(conversationId)}/copilot/session`;
const runsPath = (conversationId) => `/staff/conversations/${encodeURIComponent(conversationId)}/copilot/runs`;
const RUN_STATUS_TYPE = "data-lafayette-run-status";
const TEXT_MAX_CHARS = 65_536;
const INITIAL = Object.freeze({
    runId: null,
    text: "",
    meta: null,
    outcome: null,
    status: "STREAMING",
});
/** Folds one SSE chunk into the state. A chunk it cannot read is skipped: a stream never throws. */
function reduce(state, chunk) {
    if (typeof chunk !== "object" || chunk === null)
        return state;
    const part = chunk;
    switch (part.type) {
        case "start":
            return typeof part.messageId === "string" ? { ...state, runId: part.messageId } : state;
        case "text-delta":
            return typeof part.delta === "string" && state.text.length < TEXT_MAX_CHARS
                ? { ...state, text: (state.text + part.delta).slice(0, TEXT_MAX_CHARS) }
                : state;
        case STAFF_COPILOT_META_PART_TYPE: {
            const parsed = staffCopilotMetaPartV1Schema.safeParse(part.data);
            return parsed.success ? { ...state, meta: parsed.data } : state;
        }
        case RUN_STATUS_TYPE: {
            const outcome = part.data?.outcome;
            return typeof outcome === "object" && outcome !== null
                ? { ...state, outcome: outcome }
                : state;
        }
        case "finish":
            return { ...state, status: state.outcome === null ? "INTERRUPTED" : "COMPLETE" };
        default:
            return state;
    }
}
export function createStaffCopilotApi(options) {
    const send = createMemoryTransport(options);
    const base = options.baseUrl.endsWith("/") ? options.baseUrl : `${options.baseUrl}/`;
    return {
        getSession: (conversationId, callOptions) => send({
            method: "GET",
            path: sessionPath(conversationId),
            schema: staffCopilotSessionSchema,
            signal: callOptions?.signal,
        }),
        async run(conversationId, body, callOptions) {
            // The body is checked here too, so a mistake of the pane never leaves the page as a request.
            const checked = staffCopilotRunBodySchema.safeParse(body);
            if (!checked.success)
                throw new MemoryApiError(400, "invalid_request", "invalid copilot question", null);
            const url = new URL(runsPath(conversationId).replace(/^\//u, ""), base);
            const headers = {
                accept: "text/event-stream, application/json",
                "content-type": "application/json",
                "idempotency-key": callOptions?.idempotencyKey ?? createIdempotencyKey(),
            };
            const token = await options.getAccessToken?.();
            if (token)
                headers.authorization = `Bearer ${token}`;
            const fetcher = options.fetch ?? globalThis.fetch.bind(globalThis);
            let response;
            try {
                response = await fetcher(url, {
                    method: "POST",
                    headers,
                    body: JSON.stringify(checked.data),
                    ...(callOptions?.signal === undefined ? {} : { signal: callOptions.signal }),
                });
            }
            catch (error) {
                if (callOptions?.signal?.aborted === true)
                    throw error;
                throw new MemoryApiNetworkError({ cause: error });
            }
            if (!response.ok) {
                throw httpErrorOf(response, await readJson(response), MEMORY_ERRORS, "the copilot API refused the request");
            }
            // A key that was used before: a stored status as JSON, no stream.
            if ((response.headers.get("content-type") ?? "").includes("application/json")) {
                const json = (await response.json().catch(() => undefined));
                const parsed = agentRunStatusViewSchema.safeParse(json?.data);
                if (!parsed.success)
                    throw new MemoryApiResponseError();
                return {
                    kind: "replayed",
                    run: {
                        runId: parsed.data.runId,
                        status: parsed.data.status,
                        degradedReason: parsed.data.degradedReason,
                    },
                };
            }
            let state = INITIAL;
            const publish = (next) => {
                if (next === state)
                    return;
                state = next;
                callOptions?.onUpdate?.(state);
            };
            const parser = createSseParser();
            const read = (events) => {
                for (const event of events) {
                    if (event.kind !== "data" || event.data === "[DONE]")
                        continue;
                    try {
                        publish(reduce(state, JSON.parse(event.data)));
                    }
                    catch {
                        // A frame that is not JSON is skipped.
                    }
                }
            };
            const reader = response.body?.getReader();
            if (reader === undefined) {
                return { kind: "stream", state: { ...state, status: "INTERRUPTED" } };
            }
            const decoder = new TextDecoder();
            try {
                for (;;) {
                    const next = await reader.read();
                    if (next.done)
                        break;
                    read(parser.feed(decoder.decode(next.value, { stream: true })));
                }
                read(parser.end());
            }
            catch (error) {
                if (callOptions?.signal?.aborted === true)
                    throw error;
                // The connection broke in the middle: what arrived stays, and the run is INTERRUPTED (the server closes the row).
                publish({ ...state, status: "INTERRUPTED" });
            }
            if (state.status === "STREAMING")
                publish({ ...state, status: "INTERRUPTED" });
            return { kind: "stream", state };
        },
    };
}
